import { yupResolver } from '@hookform/resolvers/yup';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { mixed, object, string } from 'yup';

import { formatDay, IYearMonth } from '~/utils/date/date';
import { ICeEntry } from '~/services/api/communityEngagement/types';
import { DateRangeValue } from '~/ui/components/inputs/DateRangePicker/types';
import {
  CE_COMMENT_TOO_LONG,
  CE_ENGAGEMENT_DATE_IN_FUTURE,
  CE_ENGAGEMENT_END_BEFORE_START,
  CE_INVALID_CENTS,
  CE_INVALID_DOLLARS,
  CE_INVALID_HOURS,
  CE_INVALID_MINUTES,
  CE_INVALID_NUMBER,
  CE_MAX_COMMENT,
  CE_MAX_DOLLARS,
  CE_MAX_HOURS_PER_DAY,
  CE_MIN_DOLLARS,
  CE_MIN_MINUTES,
  getCeInvalidDuration,
  MINUTES_PER_HOUR,
} from '~/ui/constants/communityEngagement';
import { requiredField } from '~/ui/constants/errorMessages';

import { getOutOfMonthMessage, IEntryDateWindow, isInEntryDateWindow } from './entryDateWindow';

export interface IEntryFormValues {
  dateRange: DateRangeValue;
  durationHours: string;
  durationMinutes: string;
  currencyEarned: string;
  comment: string;
}

const toDurationFields = (totalMinutes: number | null) =>
  totalMinutes === null
    ? { durationHours: '', durationMinutes: '' }
    : {
        durationHours: String(Math.floor(totalMinutes / MINUTES_PER_HOUR)),
        durationMinutes: String(totalMinutes % MINUTES_PER_HOUR),
      };

export const getDefaultValues = (entry: ICeEntry | null): IEntryFormValues =>
  entry
    ? {
        dateRange: [parseISO(entry.startDate), parseISO(entry.endDate)],
        ...toDurationFields(entry.minutesWorked),
        currencyEarned: entry.currencyEarned === null ? '' : String(entry.currencyEarned),
        comment: entry.comment ?? '',
      }
    : {
        dateRange: [null, null],
        ...toDurationFields(null),
        currencyEarned: '',
        comment: '',
      };

const hasAtMostTwoDecimals = (value: number) =>
  Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;

const dollars = string().test('dollars', (value, { createError }) => {
  if (!value?.trim()) return true;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return createError({ message: CE_INVALID_NUMBER });
  if (parsed < CE_MIN_DOLLARS || parsed > CE_MAX_DOLLARS) {
    return createError({ message: CE_INVALID_DOLLARS });
  }
  if (!hasAtMostTwoDecimals(parsed)) return createError({ message: CE_INVALID_CENTS });
  return true;
});

export const toTotalMinutes = (hours: string, minutes: string): number | null =>
  hours.trim() === '' && minutes.trim() === ''
    ? null
    : Number(hours || 0) * MINUTES_PER_HOUR + Number(minutes || 0);

const isWholeNumber = (value: string) => /^\d*$/.test(value.trim());

const isMinutesPart = (minutes: string) =>
  isWholeNumber(minutes) && Number(minutes || 0) < MINUTES_PER_HOUR;

export const toAmount = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

const getEntryMaxHours = ([start, end]: DateRangeValue): number =>
  start && end
    ? Math.max(differenceInCalendarDays(end, start) + 1, 1) * CE_MAX_HOURS_PER_DAY
    : CE_MAX_HOURS_PER_DAY;

export const toEntryDates = ([start, end]: DateRangeValue) => {
  const startDate = start ? formatDay(start) : '';
  return { startDate, endDate: end ? formatDay(end) : startDate };
};

const toComparable = ({
  dateRange,
  durationHours,
  durationMinutes,
  ...rest
}: IEntryFormValues) => ({
  ...rest,
  dateRange: toEntryDates(dateRange),
  minutesWorked: toTotalMinutes(durationHours, durationMinutes),
});

export const isEntryFormChanged = (values: IEntryFormValues, defaults: IEntryFormValues) =>
  !isEqual(toComparable(values), toComparable(defaults));

const getSchema = (selected: IYearMonth, window: IEntryDateWindow) =>
  object().shape({
    dateRange: mixed<DateRangeValue>().test('in-window', (range, { createError }) => {
      const days = (range ?? []).flatMap(date => (date ? [formatDay(date)] : []));
      if (!days.length) return createError({ message: requiredField });
      if (days.length === 2 && days[1] < days[0]) {
        return createError({ message: CE_ENGAGEMENT_END_BEFORE_START });
      }
      if (days.some(day => day > formatDay(new Date()))) {
        return createError({ message: CE_ENGAGEMENT_DATE_IN_FUTURE });
      }
      if (!days.every(day => isInEntryDateWindow(day, window))) {
        return createError({ message: getOutOfMonthMessage(selected) });
      }
      return true;
    }),
    durationHours: string().test('ce-duration', (hours = '', { parent, createError }) => {
      if (!isWholeNumber(hours)) return createError({ message: CE_INVALID_HOURS });
      const minutes: string = parent.durationMinutes;
      const total = toTotalMinutes(hours, minutes);
      if (total === null || !isMinutesPart(minutes)) return true;
      const maxHours = getEntryMaxHours(parent.dateRange);
      return (
        (total >= CE_MIN_MINUTES && total <= maxHours * MINUTES_PER_HOUR) ||
        createError({ message: getCeInvalidDuration(maxHours) })
      );
    }),
    durationMinutes: string().test('ce-minutes', CE_INVALID_MINUTES, (minutes = '') =>
      isMinutesPart(minutes),
    ),
    currencyEarned: dollars,
    comment: string().trim().max(CE_MAX_COMMENT, CE_COMMENT_TOO_LONG),
  });

export const getEntryResolver = (selected: IYearMonth, window: IEntryDateWindow) =>
  yupResolver(getSchema(selected, window));
